// utilities/socketAuth.js
// ─────────────────────────────────────────────────────────────────────────────
// Connection options for a page that opens its OWN socket.io client.
//
// WHY THIS EXISTS (Sep-2026 dispatch audit)
// -----------------------------------------
// The server's socket auth middleware reads the JWT from
// `handshake.auth.token`, the Authorization header, or `handshake.query.token`,
// and sets `socket.data.user = null` when it finds none. Anonymous sockets are
// allowed on purpose, so nothing errors — but every gated handler then refuses
// silently:
//
//   • `joinVendorRoom` / `joinKitchenRoom` call `canAccessVendorRoom`, which
//     returns false without a user id. The ack says FORBIDDEN and the pages
//     ignore acks, so the vendor simply never joins their room and never
//     receives auto-assign feedback or scoped rider updates.
//   • `getRiders` cannot tell who is asking, so it cannot include the vendor's
//     own in-house riders.
//
// `utilities/socket.js` (the shared client) always passed `auth.token`. The
// order-details pages hand-rolled `io(url, { query: { user } })` instead — a
// user id in the query string, which the middleware does not read and would not
// trust if it did, since anyone can put any id there.
//
// Read the token at CONNECT time, never at module load: `utilities/socket.js`
// captures it in a module-level const, so a client imported before login keeps
// a null token for the life of the tab.
// ─────────────────────────────────────────────────────────────────────────────
import { getTokenFromLocalStorage } from "./tokenUtils";

/**
 * @param {object}  [opts]
 * @param {string}  [opts.userId]  kept in the query string for handlers that
 *                                 still read it; it is NOT the auth signal.
 * @returns {object} options to spread into `io(url, ...)`
 */
export const socketAuthOptions = ({ userId } = {}) => {
  const token = getTokenFromLocalStorage();
  return {
    // Polling keeps realtime working through mobile networks and proxies that
    // refuse or interrupt the initial WebSocket upgrade.
    transports: ["polling", "websocket"],
    ...(token ? { auth: { token } } : {}),
    query: {
      ...(userId ? { user: userId } : {}),
      // Belt and braces: the middleware also accepts the token here, which
      // survives proxies that strip the websocket auth payload.
      ...(token ? { token } : {}),
    },
  };
};

export default socketAuthOptions;
